import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PUSH_TITLE_MAX, PUSH_BODY_MAX } from '@wineryxplorer/shared';
import { ManagementSection } from './ManagementSection';
import { Button } from '../../components/ui/Button';
import { CharCounter } from '../../components/ui/CharCounter';
import { Typography } from '../../components/ui/Typography';
import { trpc } from '../../lib/trpc';

const inputClass =
  'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none disabled:bg-gray-100 disabled:text-gray-500';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Nov 1" from an ISO timestamp, in UTC because the monthly cap resets on UTC months. */
const formatResetDate = (iso: string) => {
  const d = new Date(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

const plural = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

const friendlyError = (message: string) =>
  message.includes('EMAIL_VERIFICATION_REQUIRED')
    ? 'Verify your email to send notifications.'
    : message;

interface ConfirmSendDialogProps {
  audienceCount: number;
  limit: number;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirm step before a send; Escape or Cancel closes it (both ignored while a send is pending). */
const ConfirmSendDialog = ({
  audienceCount,
  limit,
  isPending,
  onConfirm,
  onCancel,
}: ConfirmSendDialogProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, isPending]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="push-confirm-message"
        className="w-full max-w-md rounded-lg bg-gray-50 p-6 shadow-xl space-y-6"
      >
        <Typography variant="bodyMedium" className="text-gray-900" as="p">
          <span id="push-confirm-message">
            Send to {plural(audienceCount)}? This uses 1 of your {limit} monthly notifications.
          </span>
        </Typography>
        <div className="flex justify-end gap-3">
          <button
            ref={cancelRef}
            type="button"
            disabled={isPending}
            onClick={onCancel}
            className="min-h-[44px] min-w-[88px] rounded-md px-4 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <Button
            className="min-h-[44px]"
            loading={isPending}
            disabled={isPending}
            onClick={onConfirm}
          >
            Send now
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PushNotificationSection = () => {
  const { id } = useParams<{ id: string }>();
  const venueId = id || '';
  const utils = trpc.useUtils();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sentCount, setSentCount] = useState<number | null>(null);
  const closeConfirm = useCallback(() => setConfirming(false), []);

  const status = trpc.push.venueSendStatus.useQuery({ venueId }, { enabled: !!venueId });
  const send = trpc.push.venueSend.useMutation({
    onSuccess: (result) => {
      void utils.push.venueSendStatus.invalidate({ venueId });
      setTitle('');
      setBody('');
      setSentCount(result.recipientCount);
    },
    onSettled: () => setConfirming(false),
  });

  const data = status.data;
  const capped = !!data && !data.exempt && data.remaining === 0;
  const noAudience = !!data && data.audienceCount === 0;
  const titleOk = title.trim().length > 0 && title.length <= PUSH_TITLE_MAX;
  const bodyOk = body.trim().length > 0 && body.length <= PUSH_BODY_MAX;
  const canSend = !!data && !capped && !noAudience && titleOk && bodyOk && !send.isPending;

  const confirmSend = () => {
    send.mutate({ venueId, title: title.trim(), body: body.trim(), audience: 'favorites' });
  };

  const onTitleChange = (value: string) => {
    setTitle(value);
    send.reset();
    setSentCount(null);
  };
  const onBodyChange = (value: string) => {
    setBody(value);
    send.reset();
    setSentCount(null);
  };

  return (
    <ManagementSection
      title="Send a notification"
      description="Reach the people who favorited your venue with a deal or announcement."
      status="active"
    >
      {status.isLoading && (
        <Typography variant="bodySmall" className="text-gray-600">
          Loading notification settings...
        </Typography>
      )}

      {status.error && (
        <Typography variant="bodySmall" className="text-error">
          Unable to load notification settings: {status.error.message}
        </Typography>
      )}

      {data && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Typography variant="bodySmall" className="text-gray-700" as="p">
                Goes to the {plural(data.audienceCount)} who favorited this venue and{' '}
                {data.audienceCount === 1 ? 'allows' : 'allow'} deals notifications.
              </Typography>
              {!data.exempt && (
                <Typography variant="bodySmall" className="text-gray-700" as="p">
                  {data.usedThisMonth} of {data.limit} sent this month
                </Typography>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                loading={send.isPending}
                disabled={!canSend}
                onClick={() => setConfirming(true)}
              >
                Send
              </Button>
              {send.error && (
                <p role="alert" className="text-sm text-error text-right">
                  {friendlyError(send.error.message)}
                </p>
              )}
              {sentCount !== null && (
                <p className="text-sm text-gray-700 text-right">Sent to {plural(sentCount)}</p>
              )}
            </div>
          </div>

          {capped && (
            <Typography variant="bodySmall" className="text-gray-900" as="p">
              You've used all {data.limit} notifications for this month. Resets{' '}
              {formatResetDate(data.resetsAt)}.
            </Typography>
          )}
          {noAudience && (
            <Typography variant="bodySmall" className="text-gray-900" as="p">
              No one can receive this yet: nobody who favorited this venue has notifications on.
            </Typography>
          )}

          <div>
            <div className="flex items-baseline justify-between">
              <label htmlFor="venue-push-title" className="block text-sm font-medium text-gray-900">
                Title
              </label>
              <CharCounter length={title.length} max={PUSH_TITLE_MAX} />
            </div>
            <input
              id="venue-push-title"
              type="text"
              value={title}
              disabled={capped}
              onChange={(e) => onTitleChange(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label htmlFor="venue-push-body" className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <CharCounter length={body.length} max={PUSH_BODY_MAX} />
            </div>
            <textarea
              id="venue-push-body"
              rows={3}
              value={body}
              disabled={capped}
              onChange={(e) => onBodyChange(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {data && confirming && (
        <ConfirmSendDialog
          audienceCount={data.audienceCount}
          limit={data.limit}
          isPending={send.isPending}
          onConfirm={confirmSend}
          onCancel={closeConfirm}
        />
      )}
    </ManagementSection>
  );
};
